// src/screens/PlanDividendReduction.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Brief 46B Section 7 — Pension lever: full redesign.
// Parts A–D: Income context, personal pension redirect, tax calc, employer pension.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect } from 'react';
import { ClientData } from '../types/ClientData';
import { formatCurrency } from '../utils/format';
import { grossedUpPension, fvContributions, fvLump } from '../utils/planCalculations';
import { useCommaInput } from '../utils/useCommaInput';
import { getTaxYearForDate, TAX_YEARS, effectiveCTRate } from '../taxConfig';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

const fmt = formatCurrency;

export default function PlanDividendReduction({ data, onUpdate }: Props) {
  // ── Tax year & rates ────────────────────────────────────────────────────────
  const taxYear = data.taxYearOverride ?? getTaxYearForDate(data.sessionDate || new Date().toISOString());
  const rates = TAX_YEARS[taxYear];
  const ctRate = effectiveCTRate(data.estimatedCompanyProfit);

  // ── Income context ──────────────────────────────────────────────────────────
  const salary = data.salary || 0;
  const dividends = data.dividends || 0;
  const dividendTaxRate = rates.dividendHigherRate; // higher rate assumed for directors

  // Current dividend tax
  const taxableDivCurrent = Math.max(0, dividends - rates.dividendAllowance);
  const dividendTaxCurrent = taxableDivCurrent * dividendTaxRate;

  // ── Personal pension commitment ─────────────────────────────────────────────
  // Brief 49 S2 — initial state always starts from the clean integer user input.
  // No back-derivation from planPensionFromDividendReduction (grossed-up float)
  // which was producing fractional pre-populated values like £480.716.
  const existingAmount = data.planDividendReduction || 0;

  // personalCommitment = net monthly amount from personal take-home
  // grossedUp = personalCommitment / (1 - ctRate) — what goes into pension
  const handleAmountChange = (newPersonal: number) => {
    const grossed = newPersonal > 0 ? grossedUpPension(newPersonal, ctRate) : 0;
    const annualGrossed = grossed * 12;
    const reducedDividends = Math.max(0, dividends - newPersonal * 12);
    const taxableDivReduced = Math.max(0, reducedDividends - rates.dividendAllowance);
    const dividendTaxReduced = taxableDivReduced * dividendTaxRate;
    const divTaxSaved = dividendTaxCurrent - dividendTaxReduced;
    const ctSaved = annualGrossed * ctRate;
    const totalSaved = divTaxSaved + ctSaved;
    const yearsToFreedom = Math.max(0, (data.targetFreedomAge || 55) - (data.clientAge || 40));
    const cumSaved = totalSaved * yearsToFreedom;

    const growthPct = (data.assumptionPensionGrowth || 0.08) * 100;
    const dnFV = fvLump(data.pensionBalance || 0, growthPct, yearsToFreedom) +
      fvContributions(data.pensionCompanyFunded || 0, growthPct, yearsToFreedom);
    const planFV = fvLump(data.pensionBalance || 0, growthPct, yearsToFreedom) +
      fvContributions((data.pensionCompanyFunded || 0) + grossed, growthPct, yearsToFreedom);

    onUpdate({
      planDividendReduction: Math.round(newPersonal),
      planDividendReductionCommitted: newPersonal > 0,
      planPensionFromDividendReduction: grossed,
      planTaxSavedPerYear: totalSaved,
      planCumulativeTaxSaved: cumSaved,
      totalTaxSavedAnnual: totalSaved,
      totalTaxSavedCumulative: cumSaved,
      totalGapClosed: Math.max(0, planFV - dnFV),
    });
  };

  const inputProps = useCommaInput(existingAmount, handleAmountChange, true);

  // Derived values for display
  const personalCommitment = existingAmount;
  const grossedUp = personalCommitment > 0 ? grossedUpPension(personalCommitment, ctRate) : 0;
  const annualCommitment = personalCommitment * 12;
  const annualGrossed = grossedUp * 12;

  // Tax calcs
  const reducedDividends = Math.max(0, dividends - annualCommitment);
  const taxableDivReduced = Math.max(0, reducedDividends - rates.dividendAllowance);
  const dividendTaxReduced = taxableDivReduced * dividendTaxRate;
  const divTaxSavedAnnual = dividendTaxCurrent - dividendTaxReduced;
  const ctSavedAnnual = annualGrossed * ctRate;
  const netEffectiveCost = annualCommitment - divTaxSavedAnnual;

  const yearsToFreedom = Math.max(0, (data.targetFreedomAge || 55) - (data.clientAge || 40));

  // Sync tally on mount
  useEffect(() => {
    if (existingAmount > 0) {
      const totalSaved = divTaxSavedAnnual + ctSavedAnnual;
      onUpdate({
        totalTaxSavedAnnual: totalSaved,
        totalTaxSavedCumulative: totalSaved * yearsToFreedom,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Employer pension
  const employerPensionMonthly = data.pensionCompanyFunded || 0;

  return (
    <div className="min-h-screen flex">
      {/* ── LEFT PANEL ─────────────────────────────────────────────────────── */}
      <div className="w-2/5 bg-ma-navy text-white p-10 flex flex-col justify-center">
        {/* Part A — Current income context */}
        <div className="mb-8">
          <div className="text-xs uppercase tracking-widest text-white/50 mb-2">Current Personal Income</div>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-white/60 text-sm">Salary</span>
              <span className="text-lg font-bold">{fmt(salary)}/yr</span>
            </div>
            <div className="flex justify-between">
              <span className="text-white/60 text-sm">Dividends</span>
              <span className="text-lg font-bold">{fmt(dividends)}/yr</span>
            </div>
            <div className="flex justify-between border-t border-white/20 pt-2">
              <span className="text-white/60 text-sm">Dividend tax (higher rate)</span>
              <span className="text-lg font-bold text-red-300">{fmt(Math.round(dividendTaxCurrent))}/yr</span>
            </div>
          </div>
        </div>

        <div className="border-t border-white/20 my-6" />

        {/* Part C — Tax saving summary */}
        {personalCommitment > 0 && (
          <div className="mb-6">
            <div className="text-xs uppercase tracking-widest text-white/50 mb-2">Tax Saved Per Year</div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-white/60 text-sm">Dividend tax saved ({(dividendTaxRate * 100).toFixed(2)}%)</span>
                <span className="font-bold text-green-400">{fmt(Math.round(divTaxSavedAnnual))}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/60 text-sm">CT saved ({(ctRate * 100).toFixed(0)}%)</span>
                <span className="font-bold text-green-400">{fmt(Math.round(ctSavedAnnual))}</span>
              </div>
              <div className="flex justify-between border-t border-white/20 pt-2">
                <span className="text-white/50 text-sm font-semibold">Total annual saving</span>
                <span className="text-xl font-bold text-green-400">{fmt(Math.round(divTaxSavedAnnual + ctSavedAnnual))}</span>
              </div>
            </div>
          </div>
        )}

        <div className="border-t border-white/20 my-6" />

        {/* Net effective cost */}
        {personalCommitment > 0 && (
          <div className="mb-6">
            <div className="text-xs uppercase tracking-widest text-white/50 mb-1">Net Effective Cost To You</div>
            <div className="text-3xl font-bold" style={{ color: '#C9A84C' }}>{fmt(Math.round(netEffectiveCost))}/yr</div>
            <p className="text-xs text-white/40 mt-1">Annual commitment minus dividend tax saved</p>
          </div>
        )}

        {/* Part D — Existing employer pension */}
        <div className="mt-auto pt-8 border-t border-white/20">
          <div className="text-xs uppercase tracking-widest text-white/50 mb-1">Existing Employer Pension</div>
          <div className="text-lg font-bold text-white">{fmt(employerPensionMonthly)}/month</div>
          <p className="text-xs text-white/30 mt-1">Session 2 will optimise your employer pension schedule.</p>
        </div>
      </div>

      {/* ── RIGHT PANEL ────────────────────────────────────────────────────── */}
      <div className="w-3/5 bg-white p-10 flex flex-col justify-center">
        <h3 className="text-xl font-bold text-ma-navy mb-2">Pension Contribution</h3>
        <p className="text-sm text-gray-500 mb-6">
          Any income you redirect to pension saves dividend tax AND the company saves CT on the contribution.
        </p>

        {/* Part B — Personal pension redirect input */}
        <div className="mb-8">
          <label className="block text-sm font-semibold text-gray-900 mb-2">
            How much monthly income do you want to commit to pension?
          </label>
          <p className="text-xs text-gray-400 mb-1">
            Current personal pension contribution: {fmt(data.pensionMonthly ?? 0)}/month
          </p>
          <p className="text-xs text-gray-500 mb-3">
            This comes from your personal take-home — we calculate the effective cost below.
          </p>
          <div className="flex items-center gap-2">
            <span className="text-lg font-semibold text-gray-900">£</span>
            <input
              {...inputProps}
              placeholder="0"
              className="w-40 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ma-navy text-lg"
            />
            <span className="text-gray-600">/month</span>
          </div>
        </div>

        {/* Part C — Tax saving calculation (dynamic) */}
        {personalCommitment > 0 && (
          <div className="bg-gray-50 rounded-xl border border-gray-200 p-6 mb-6">
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-4">Tax Saving Calculation</h4>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Monthly gross commitment</span>
                <span className="font-medium">{fmt(Math.round(personalCommitment))}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Annual commitment</span>
                <span className="font-medium">{fmt(Math.round(annualCommitment))}</span>
              </div>

              <div className="border-t border-gray-200 pt-3">
                <div className="text-xs text-gray-400 uppercase tracking-wide mb-2">Tax saved per year</div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Dividend tax saved ({(dividendTaxRate * 100).toFixed(2)}%)</span>
                  <span className="font-medium text-green-600">{fmt(Math.round(divTaxSavedAnnual))}</span>
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-gray-600">Corporation tax saved ({(ctRate * 100).toFixed(0)}%)</span>
                  <span className="font-medium text-green-600">{fmt(Math.round(ctSavedAnnual))}</span>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Grossed-up pension contribution</span>
                  <span className="font-bold text-ma-navy">{fmt(Math.round(annualGrossed))}/yr</span>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  Company pays {fmt(Math.round(grossedUp))}/month gross so the net cost after CT = {fmt(Math.round(personalCommitment))}/month
                </p>
              </div>

              <div className="border-t border-gray-200 pt-3">
                <div className="flex justify-between font-semibold">
                  <span className="text-gray-700">Net effective cost to you</span>
                  <span className="text-ma-navy">{fmt(Math.round(netEffectiveCost))}/yr</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* CT saving note */}
        {personalCommitment > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6 text-xs text-gray-600">
            CT saving of {fmt(Math.round(ctSavedAnnual))} per year noted — this commitment carries forward
            to Session 2 where we model the full employer pension schedule.
          </div>
        )}

        {/* Annual allowance check */}
        {(() => {
          const existingAnnual = employerPensionMonthly * 12;
          const proposed = existingAnnual + annualGrossed;
          return proposed > 60000 && personalCommitment > 0 ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6 text-xs text-amber-900">
              <span className="font-semibold">⚠ Annual pension allowance warning</span> — total contributions
              ({fmt(Math.round(proposed))}/yr) approach the £60,000 annual limit. We'll confirm headroom in Session 2.
            </div>
          ) : null;
        })()}

        {/* CT rate context */}
        <div className="text-xs text-gray-400 mt-auto">
          {data.estimatedCompanyProfit && data.estimatedCompanyProfit > 0 ? (
            <p>CT rate: {(ctRate * 100).toFixed(1)}% (based on estimated profit of {fmt(data.estimatedCompanyProfit)}/yr)</p>
          ) : (
            <p>CT rate: 19% (default — enter estimated profit in Income for a more precise figure)</p>
          )}
        </div>
      </div>
    </div>
  );
}
